import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { settingsService, WorkspaceInfo } from '@/services/settings.service';
import { CrmLabel, GENERAL_PAGES, LABEL_DEFAULTS, LabelKey, PageKey } from '@/lib/crm-pages';

interface WorkspaceCtx {
  info: WorkspaceInfo | null;
  loaded: boolean;
  /** Whether a page shows for this company (blueprint + what the admin hid). */
  pageOn: (page: PageKey) => boolean;
  /** The company's word for a thing: label('contacts') → "Guests". */
  label: (key: LabelKey, form?: 'plural' | 'singular') => string;
  labels: Record<LabelKey, CrmLabel>;
  /** The company's currency (null until the workspace answers). */
  currency: string | null;
  refresh: () => Promise<void>;
  /** Swap in a fresh answer (e.g. the response of a save) without refetching. */
  set: (next: WorkspaceInfo) => void;
}

const WorkspaceContext = createContext<WorkspaceCtx | null>(null);

// Last answer per company, so a refresh renders the company's own words at once
// instead of flashing "Contacts" before "Guests".
const cacheKey = (companyUid: string) => `slightor_workspace_${companyUid}`;
const readCache = (companyUid?: string | null): WorkspaceInfo | null => {
  if (!companyUid) return null;
  try {
    const raw = localStorage.getItem(cacheKey(companyUid));
    return raw ? (JSON.parse(raw) as WorkspaceInfo) : null;
  } catch {
    return null;
  }
};

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const companyUid = user?.companyUid;
  const [info, setInfo] = useState<WorkspaceInfo | null>(() => readCache(companyUid));
  const [loaded, setLoaded] = useState(false);

  const set = useCallback((next: WorkspaceInfo) => {
    setInfo(next);
    if (companyUid) {
      try { localStorage.setItem(cacheKey(companyUid), JSON.stringify(next)); } catch { /* storage full or blocked */ }
    }
  }, [companyUid]);

  const refresh = useCallback(async () => {
    if (!companyUid || user?.role === 'super_admin') { setLoaded(true); return; }
    try {
      set(await settingsService.workspace());
    } catch {
      /* keep the cached or default layout — the sidebar must never lock a tenant out */
    } finally {
      setLoaded(true);
    }
  }, [companyUid, user?.role, set]);

  useEffect(() => {
    setInfo(readCache(companyUid));
    void refresh();
  }, [companyUid, refresh]);

  const value = useMemo<WorkspaceCtx>(() => {
    const labels = { ...LABEL_DEFAULTS, ...(info?.labels ?? {}) } as Record<LabelKey, CrmLabel>;
    return {
      info,
      loaded,
      labels,
      pageOn: (page) => (info?.pages ? info.pages[page] !== false : GENERAL_PAGES[page] !== false),
      label: (key, form = 'plural') => labels[key]?.[form] ?? LABEL_DEFAULTS[key][form],
      currency: info?.currency ?? info?.defaultCurrency ?? null,
      refresh,
      set,
    };
  }, [info, loaded, refresh, set]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

const FALLBACK: WorkspaceCtx = {
  info: null,
  loaded: true,
  labels: LABEL_DEFAULTS,
  pageOn: (page) => GENERAL_PAGES[page] !== false,
  label: (key, form = 'plural') => LABEL_DEFAULTS[key][form],
  currency: null,
  refresh: async () => {},
  set: () => {},
};

/** Workspace facts + the company's words. Outside the provider (public pages) it falls back to the General CRM. */
export function useWorkspace(): WorkspaceCtx {
  return useContext(WorkspaceContext) ?? FALLBACK;
}

/**
 * The company's currency for NEW records and displays. Falls back to the
 * given value only while the workspace is still loading.
 */
export function useCompanyCurrency(fallback = 'USD'): string {
  return useWorkspace().currency ?? fallback;
}

/**
 * A currency form field that follows the company's currency until the user
 * (or loaded data) sets one — so a form opened before the workspace answers
 * never saves the fallback.
 */
export function useCurrencyField(initial?: string | null): [string, (next: string) => void] {
  const company = useCompanyCurrency();
  const [value, setValue] = useState<string | null>(initial ?? null);
  return [value ?? company, setValue];
}

/**
 * The company's country (ISO-2) for phone and import defaults. The CRM used to hard-code Pakistan, so a UAE
 * company's contacts and CSV imports started on +92.
 */
export function useCompanyCountry(fallback = 'AE'): string {
  return useWorkspace().info?.country || fallback;
}

/** Shorthand: `const t = useLabels(); t('contacts')` → "Guests". */
export function useLabels() {
  return useWorkspace().label;
}
